/**
 * `compile(document) → { body, warnings }` — the one-way path from the design
 * document to the canonical Twig/HTML body.
 *
 * Pure and deterministic: same document in, byte-identical body out. That is
 * what makes the round-trip assertion
 * `loadDocument(compile(doc).body, layoutKey, doc)` an equality check, and
 * what lets the canvas render the *real* email rather than a React
 * approximation of it. The document itself is stored beside the body
 * (`design_document`), so the body carries markup only.
 *
 * Emitted shape mirrors `EmailTemplate::renderEmail`, which builds
 * `"{% extends '<layout_key>' %}\n" . $body` at render time. The **extends tag
 * therefore must not appear in the body** — the layout is the separate
 * `layout_key` column, and a body carrying its own extends would give Twig two
 * of them ("Multiple extends tags are forbidden"). A layouted document compiles
 * to `{% block %}` overrides only; `renderTwigPreview` wraps them via its
 * `layoutKey` option exactly as the server does.
 *
 * Core components (`core.*`, Twig files seeded as global template fragments)
 * land in Sprint 1; this build emits the raw-HTML primitive and reports
 * anything else as a warning rather than silently dropping it.
 */
import { EMBEDDED_EXPRESSION } from '../twigExpressions';
import { EmailVariableGroup } from '../types';
import {
  Block,
  Condition,
  ConditionClause,
  DEFAULT_LAYOUT_SLOT,
  EmailDocument,
  NATIVE_BLOCK,
  ThemeableColor,
} from './document';
import {
  ManifestParam,
  ManifestSlot,
  isExpressionValue,
  unwrapExpression,
} from './manifest';
import { BlockDefinition, EMPTY_REGISTRY, Registry } from './registry';
import {
  conditionScope,
  deferStatement,
  isSendTimeExpression,
  sendTimeRoots,
} from './sendTime';

/** Defined with the document model, which needs it when loading a layouted body. */
export { DEFAULT_LAYOUT_SLOT };

/**
 * One level of indentation in the emitted body.
 *
 * The compiler produces the source a developer reads in the editor's Source panel
 * and in a code review, so it indents its own output rather than leaning on a
 * formatter. Nothing downstream cares — Twig ignores whitespace between tags — and
 * doing it here means the *stored* body is readable, not just one view of it.
 */
const INDENT = '  ';

/**
 * Beyond this, a component's `with { … }` hash is broken one argument per line.
 *
 * A `core.button` include is ~200 characters on one line, which is where the
 * unreadability actually lives. Multi-line tags are safe in **both** engines —
 * verified 2026-07-28 against twig.js (the stricter one) and PHP Twig 3.27.1,
 * indented and inside `embed` as well as `include`.
 */
const MAX_LINE = 100;

/** Indents every line of `markup`; used for structure, never for raw content. */
function indent(markup: string, depth: number): string {
  if (depth <= 0) return markup;
  const pad = INDENT.repeat(depth);
  return markup
    .split('\n')
    .map((line) => (line.length > 0 ? pad + line : line))
    .join('\n');
}

/**
 * Params the compiler injects, which components read but never declare. The
 * `_cvn_` prefix is reserved — `manifest.ts` rejects a declared param using it.
 */
export const BLOCK_ID_PARAM = '_cvn_id';

/**
 * Attribute each core component renders from `_cvn_id`.
 *
 * This is the *only* hook the canvas overlay needs: it measures
 * `[data-cvn-id]` rects inside the preview iframe and maps each back to a
 * block, which is what lets the editing surface be the real rendered email
 * rather than a React re-implementation of it.
 *
 * It ships in the stored body and therefore in real sends. That is deliberate —
 * an inert attribute is the price of the canvas and the sent email being
 * byte-identical. A `stripEditorAttrs` pass can be added if a client ever
 * objects.
 */
export const BLOCK_ID_ATTRIBUTE = 'data-cvn-id';

/**
 * Attribute a *container* component renders on each slot region, valued
 * `<blockId>:<slotIndex>`.
 *
 * Without it an empty slot has no geometry — nothing is rendered inside it to
 * measure — so dropping into an empty column would be impossible on the canvas
 * and only reachable through the layers tree. Slot indices are positional,
 * matching the component's `@slot` declaration order.
 */
export const SLOT_ATTRIBUTE = 'data-cvn-slot';

/**
 * Comment markers bracketing a block that does not render its own `data-cvn-id`.
 *
 * The canvas hit-tests measured rects, so a block with nothing identifiable in the DOM
 * cannot be hovered, selected, dragged, or even *dropped next to* — its siblings are
 * filtered out of the drop resolver for having no rect. That is every partner partial
 * and every raw-HTML block, which is what made them tree-only.
 *
 * **Why comments rather than a wrapper element.** A wrapper is illegal where these
 * blocks live: `partials/xxxl/promotion` emits a bare `<td>`, and a `<div>` around it
 * would be foster-parented out of the table, moving the content. A comment is valid in
 * every context including inside `<table>`, changes no layout, and is inert in email
 * clients. Verified to survive twig.js, in and out of table context.
 *
 * **Why in the stored body.** `data-cvn-id` already ships to recipients for every core
 * component — a deliberate, documented trade (see `BLOCK_ID_ATTRIBUTE`) — so this
 * establishes no new principle, and keeping one body means the canvas, the Source view
 * and the send can never disagree.
 *
 * The one hazard is nesting: a `-->` inside an enclosing comment would end it early.
 * These are emitted *around* an include, at the body's own top level, and the two real
 * layouts place their regions in flow content (checked, not assumed). A layout that put
 * a region inside a conditional comment would already be hiding all of its content from
 * every non-Outlook client, so it is not a shape to design for.
 */
export const blockMarkerStart = (blockId: string) => `<!--cvn:${blockId}-->`;
export const blockMarkerEnd = (blockId: string) => `<!--/cvn:${blockId}-->`;

/** Matches either marker, capturing the block id — used to strip and to measure. */
export const BLOCK_MARKER_PATTERN = /<!--\/?cvn:([A-Za-z0-9_-]+)-->/g;

export const COMPILE_WARNING = {
  UNSUPPORTED_BLOCK: 'unsupported-block',
  EMPTY_CONDITION: 'empty-condition',
  MISSING_REQUIRED_PROP: 'missing-required-prop',
  UNSERIALIZABLE_PROP: 'unserializable-prop',
  INLINE_UNSUPPORTED: 'inline-unsupported',
  MIXED_CONDITION_SCOPE: 'mixed-condition-scope',
} as const;

export interface CompileWarning {
  code: string;
  /** The block the warning came from, when it is block-scoped. */
  blockId?: string;
  /** Untranslated, developer-facing; the UI maps `code` to a message. */
  detail: string;
}

export interface CompileResult {
  body: string;
  warnings: CompileWarning[];
}

/** Twig single-quoted string literal. */
function quote(value: string): string {
  return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/**
 * A string with `{{ … }}` **inside** it, as a Twig concatenation.
 *
 * Why this exists: a prop is passed to a component as a *value*, and Twig does not
 * re-parse a string argument. So `html: 'Hi {{ customer.firstname }}'` reaches
 * `{{ html|raw }}` as those literal characters and the recipient reads the braces.
 * Quoting the whole thing is therefore wrong for any text that mixes words with a
 * variable — which is exactly what the rich editor's variable and translation
 * dropdowns produce, and what anyone typing a variable into a text field expects to
 * work.
 *
 * `'Hi ' ~ customer.firstname` is the Twig for it. Verified in **both** engines
 * before relying on it (twig.js and PHP Twig 3, including a filter inside the
 * concatenation and an undefined variable, which renders empty rather than throwing).
 *
 * Expression parts are parenthesised: filters already bind tighter than `~` in both
 * engines, but the parentheses make that independent of precedence rather than
 * dependent on it.
 */
function emitInterpolated(value: string, sendTimeRoots: Set<string>): string {
  const parts: string[] = [];
  let cursor = 0;

  for (const match of value.matchAll(EMBEDDED_EXPRESSION)) {
    const expression = match[1].trim();
    // `{{ }}` with nothing in it is not an expression; leave it as the text it is.
    if (!expression) continue;

    /**
     * **A send-time variable stays literal text.** Concatenating it would make
     * *our* render evaluate it — against a payload with no recipient in it — so
     * it resolves to nothing and the words around it close up. The braces are
     * not read by anyone: the provider fills them when it sends. Skipping the
     * split leaves this tag inside the quoted string, where `SendTimeTags`
     * protects it through the render like any other output tag.
     */
    if (isSendTimeExpression(expression, sendTimeRoots)) continue;

    const literal = value.slice(cursor, match.index);
    if (literal) parts.push(quote(literal));
    parts.push(`(${expression})`);
    cursor = (match.index ?? 0) + match[0].length;
  }

  // No embedded expression: an ordinary string literal, quoted as before.
  if (parts.length === 0) return quote(value);

  const tail = value.slice(cursor);
  if (tail) parts.push(quote(tail));

  return parts.join(' ~ ');
}

/**
 * A compared value as Twig: a number stays bare, anything else is quoted.
 *
 * This is what makes `>` mean *greater* rather than *later in the alphabet* —
 * `count > '9'` compares strings, so 10 would lose to 9. Emitting `count > 9`
 * gets numeric comparison from Twig and from twig.js in the preview alike.
 */
function compileComparand(value: string | undefined): string {
  const raw = (value ?? '').trim();
  if (raw !== '' && Number.isFinite(Number(raw))) return raw;
  return quote(value ?? '');
}

function compileClause(clause: ConditionClause): string {
  const comparand = compileComparand(clause.value);
  switch (clause.op) {
    case 'unset':
      return `not ${clause.path}`;
    case 'eq':
      return `${clause.path} == ${comparand}`;
    case 'neq':
      return `${clause.path} != ${comparand}`;
    // Twig's `in` is haystack-last and reads as containment for strings and
    // lists alike, so one operator covers "the branch name has 'Nord' in it"
    // and "this tag is among the lead's tags".
    case 'contains':
      return `${comparand} in ${clause.path}`;
    case 'ncontains':
      return `${comparand} not in ${clause.path}`;
    case 'gt':
      return `${clause.path} > ${comparand}`;
    case 'gte':
      return `${clause.path} >= ${comparand}`;
    case 'lt':
      return `${clause.path} < ${comparand}`;
    case 'lte':
      return `${clause.path} <= ${comparand}`;
    case 'set':
    default:
      return clause.path;
  }
}

/**
 * A block's visibility condition as a Twig boolean expression. Clauses are
 * parenthesised so mixing `and`/`or` can't change meaning through operator
 * precedence, matching the shape `extractIfConditionVars` and
 * `evaluateLiteralConditionals` already read for the preview's condition
 * toggles.
 */
export function compileCondition(condition: Condition): string | null {
  const clauses = condition.clauses.filter((clause) => clause.path);
  if (clauses.length === 0) return null;

  const joiner = condition.mode === 'any' ? ' or ' : ' and ';
  return clauses.map((clause) => `(${compileClause(clause)})`).join(joiner);
}

/** A theme-bound colour resolves to the Twig path the layouts already read. */
function isThemeableColor(value: unknown): value is ThemeableColor {
  if (value === null || typeof value !== 'object') return false;
  return 'token' in value || 'literal' in value;
}

/**
 * One `with { … }` argument value. Returns null when the value cannot be
 * expressed, so the caller can warn instead of emitting broken Twig.
 *
 * Strings that look like `{{ … }}` are emitted as expressions rather than
 * quoted literals — that is how a prop carries `{{ theme.colors.brand }}`.
 */
function emitValue(value: unknown, sendTimeRoots: Set<string>): string | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number')
    return Number.isFinite(value) ? String(value) : null;

  if (isThemeableColor(value)) {
    return 'token' in value && value.token
      ? `theme.colors.${value.token}`
      : quote(String((value as { literal: string }).literal));
  }

  if (typeof value === 'string') {
    if (isExpressionValue(value)) {
      const expression = unwrapExpression(value);

      // Same reasoning as emitInterpolated: unwrapping a send-time variable into
      // a bare expression hands it to our own render, which resolves it to
      // nothing. Quoted, it travels to the provider as the tag it is.
      return isSendTimeExpression(expression, sendTimeRoots)
        ? quote(value.trim())
        : expression;
    }
    return emitInterpolated(value, sendTimeRoots);
  }

  // A structured value (a `spacing` prop's {top,right,bottom,left}, or any
  // nested object a component reads with dotted access) becomes a Twig hash
  // literal. Keys are emitted in sorted order so the compiled body stays
  // deterministic regardless of how the editor happened to build the object.
  if (typeof value === 'object' && !Array.isArray(value)) {
    const pairs: string[] = [];
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const nested = emitValue(
        (value as Record<string, unknown>)[key],
        sendTimeRoots,
      );
      if (nested !== null) pairs.push(`${key}: ${nested}`);
    }
    return pairs.length > 0 ? `{ ${pairs.join(', ')} }` : null;
  }

  return null;
}

/**
 * The `with { … }` argument list. Declared params are emitted in manifest order
 * so output is deterministic; an undeclared fragment falls back to its props
 * sorted by name for the same reason. A prop equal to its declared default is
 * still emitted — the document is the source of truth, and silently relying on
 * a component-side default would make the compiled body change meaning if that
 * default ever moved.
 */
function emitArguments(
  block: Block,
  definition: BlockDefinition,
  warnings: CompileWarning[],
  depth: number,
  /** Length of the `{% include 'key'` prefix, so wrapping can measure the line. */
  prefixLength: number,
  sendTimeRoots: Set<string>,
): string {
  const props = block.props ?? {};
  const names = definition.declared
    ? definition.params.map((param: ManifestParam) => param.name)
    : Object.keys(props).sort();

  // Injected first so it stays stable regardless of which props are set —
  // the overlay depends on it being present on every component.
  const pairs: string[] = [`${BLOCK_ID_PARAM}: ${quote(block.id)}`];
  for (const name of names) {
    const emitted = emitValue(props[name], sendTimeRoots);
    if (emitted === null) {
      const param = definition.params.find((each) => each.name === name);
      if (param?.required) {
        warnings.push({
          code: COMPILE_WARNING.MISSING_REQUIRED_PROP,
          blockId: block.id,
          detail: `"${name}" is required by ${definition.type} but has no value`,
        });
      } else if (name in props && props[name] !== '' && props[name] != null) {
        warnings.push({
          code: COMPILE_WARNING.UNSERIALIZABLE_PROP,
          blockId: block.id,
          detail: `"${name}" of ${definition.type} cannot be expressed in Twig`,
        });
      }
      continue;
    }
    pairs.push(`${name}: ${emitted}`);
  }

  const inline = ` with { ${pairs.join(', ')} }`;
  if (prefixLength + inline.length + ' %}'.length <= MAX_LINE) return inline;

  // One argument per line. The closing brace sits at the tag's own indent so the
  // block reads as a unit.
  const pad = INDENT.repeat(depth + 1);
  const close = INDENT.repeat(depth);
  return ` with {\n${pairs
    .map((pair) => `${pad}${pair},`)
    .join('\n')}\n${close}}`;
}

/**
 * A Twig component: `{% include %}` when it takes no children, `{% embed %}`
 * with `{% block %}` overrides when a slot is filled. Passing child markup
 * through blocks rather than as a string argument avoids escaping whole
 * subtrees into a Twig string literal — verified working in twig.js and native
 * in PHP Twig.
 */
function emitComponent(
  block: Block,
  definition: BlockDefinition,
  registry: Registry,
  warnings: CompileWarning[],
  depth: number,
  mode: EmitMode,
): string {
  const key = definition.fragmentKey as string;
  const pad = INDENT.repeat(depth);
  const args = emitArguments(
    block,
    definition,
    warnings,
    depth,
    `${pad}{% include ${quote(key)}`.length,
    mode.sendTimeRoots,
  );

  if (definition.inline) {
    // Compile-time expansion is declared but not implemented: `{% include %}`
    // is still correct, just not self-contained. Warn rather than pretend.
    warnings.push({
      code: COMPILE_WARNING.INLINE_UNSUPPORTED,
      blockId: block.id,
      detail: `${definition.type} declares @inline; emitted as an include instead`,
    });
  }

  const filled: { slot: ManifestSlot; children: Block[] }[] = [];
  (block.children ?? []).forEach((children, index) => {
    const slot = definition.slots[index];
    if (slot && children.length > 0) filled.push({ slot, children });
  });

  if (filled.length === 0) {
    return `${pad}{% include ${quote(key)}${args} %}`;
  }

  const blockPad = INDENT.repeat(depth + 1);
  const blocks = filled.map(
    (entry) =>
      `${blockPad}{% block ${entry.slot.name} %}\n${emitBlocks(
        entry.children,
        registry,
        warnings,
        depth + 2,
        mode,
      )}\n${blockPad}{% endblock %}`,
  );

  return [
    `${pad}{% embed ${quote(key)}${args} %}`,
    ...blocks,
    `${pad}{% endembed %}`,
  ].join('\n');
}

/**
 * Per-block emit cache: `registry → block → { markup, warnings }`.
 *
 * This is what `tree.ts`'s structural sharing is *for*. Every reducer action
 * rebuilds only the spine down to the block it touched and returns the identical
 * array (and therefore the identical block objects) everywhere else, so an
 * unchanged subtree is `===` its previous self and its markup can be reused —
 * including a container's whole subtree in one lookup.
 *
 * Two conditions make this safe, and both are already invariants:
 *
 * - **Blocks are never mutated.** Every change produces new objects, so a cached
 *   entry cannot go stale under a block that still compares equal.
 * - **Warnings are cached with the markup and replayed**, not skipped. Otherwise a
 *   cache hit would silently drop a *missing required prop* warning and preflight
 *   would stop blocking the save on the second compile.
 *
 * `WeakMap` throughout: entries disappear with the documents that held them, so an
 * editing session does not accumulate the markup of every intermediate state.
 *
 * **Keyed by depth as well**, since the markup is indented at emit time: the same
 * block dragged from a section into a column indents differently, and a
 * depth-blind cache would hand back the old indentation. Depth is the innermost key
 * because it is the one that changes least — a block's depth only moves when it is
 * dragged, so the common edit still hits.
 */
interface CachedEmit {
  markup: string;
  warnings: CompileWarning[];
}

const emitCache = new WeakMap<
  Registry,
  WeakMap<Block, Map<string, CachedEmit>>
>();

/**
 * How one emit differs from another. Both flags change the emitted bytes, so both are
 * part of the block cache key below.
 */
interface EmitMode {
  /** Bracket a block that cannot mark itself with `<!--cvn:id-->` comments. */
  markers: boolean;
  /** Emit conditional blocks unwrapped — see `CompileContext.ignoreVisibility`. */
  ignoreVisibility: boolean;
  /**
   * Roots the provider fills per recipient, from `CompileContext.variableGroups`.
   * A condition over one of these is deferred to send time; see sendTime.ts.
   */
  sendTimeRoots: Set<string>;
  /**
   * `sendTimeRoots` as a stable string, for the emit cache key.
   *
   * The roots change what a block's bytes are — a send-time variable in a text
   * prop is emitted as literal text rather than concatenated — so a block
   * emitted before the catalogue landed must not be replayed after it. Computed
   * once per compile rather than per block.
   */
  sendTimeKey: string;
}

/**
 * Cached emissions for one block, keyed by
 * `depth:markers:ignoreVisibility:sendTimeRoots` — each of the four changes the bytes,
 * so a single key would replay the wrong variant into the body.
 */
function cacheFor(registry: Registry, block: Block): Map<string, CachedEmit> {
  let perBlock = emitCache.get(registry);
  if (!perBlock) {
    perBlock = new WeakMap<Block, Map<string, CachedEmit>>();
    emitCache.set(registry, perBlock);
  }
  let byKey = perBlock.get(block);
  if (!byKey) {
    byKey = new Map<string, CachedEmit>();
    perBlock.set(block, byKey);
  }
  return byKey;
}

function emitBlock(
  block: Block,
  registry: Registry,
  warnings: CompileWarning[],
  depth: number,
  mode: EmitMode,
): string {
  const cache = cacheFor(registry, block);
  const key = `${depth}:${mode.markers}:${mode.ignoreVisibility}:${mode.sendTimeKey}`;
  const cached = cache.get(key);
  if (cached) {
    // Replay, so a cache hit is indistinguishable from a fresh emit.
    warnings.push(...cached.warnings);
    return cached.markup;
  }

  const own: CompileWarning[] = [];
  const markup = emitBlockUncached(block, registry, own, depth, mode);
  cache.set(key, { markup, warnings: own });
  warnings.push(...own);
  return markup;
}

/**
 * Brackets an emission with comment markers when the component cannot mark itself.
 *
 * The markers take the block's own indent and the content keeps its; nothing about the
 * emitted markup between them changes, so a body gains exactly two comments per
 * unmarked block and nothing else.
 */
function withMarkers(
  markup: string,
  block: Block,
  registry: Registry,
  depth: number,
  mode: EmitMode,
): string {
  if (!mode.markers || !markup) return markup;
  if (registry.definitions[block.type]?.rendersOwnMarker) return markup;

  const pad = INDENT.repeat(depth);
  return [
    `${pad}${blockMarkerStart(block.id)}`,
    markup,
    `${pad}${blockMarkerEnd(block.id)}`,
  ].join('\n');
}

function emitBlockUncached(
  block: Block,
  registry: Registry,
  warnings: CompileWarning[],
  depth: number,
  mode: EmitMode,
): string {
  if (block.type === NATIVE_BLOCK.HTML) {
    // Passed through verbatim: this block holds hand-written Twig and whole
    // legacy documents, which must stay byte-identical. No `data-cvn-id` is
    // injected — there is no safe insertion point in arbitrary markup, and the
    // canvas treats a raw-HTML block as one opaque card.
    //
    // Only the *first* line takes the indent, so the content's own lines keep
    // their exact bytes — re-indenting inside pasted markup would change what a
    // `<pre>` renders, and would churn the body every time the block is moved.
    const html = typeof block.props.html === 'string' ? block.props.html : '';
    return withMarkers(
      html ? `${INDENT.repeat(depth)}${html}` : '',
      block,
      registry,
      depth,
      mode,
    );
  }

  const definition = registry.definitions[block.type];
  if (definition?.fragmentKey) {
    return withMarkers(
      emitComponent(block, definition, registry, warnings, depth, mode),
      block,
      registry,
      depth,
      mode,
    );
  }

  warnings.push({
    code: COMPILE_WARNING.UNSUPPORTED_BLOCK,
    blockId: block.id,
    detail: `No emitter for block type "${block.type}"`,
  });
  return '';
}

function emitBlocks(
  blocks: Block[],
  registry: Registry,
  warnings: CompileWarning[],
  depth: number,
  mode: EmitMode,
): string {
  const parts: string[] = [];
  const pad = INDENT.repeat(depth);

  for (const block of blocks) {
    /**
     * Resolved *before* emitting, because whether the block ends up inside an
     * `{% if %}` decides how deep it is indented — and a condition with no usable
     * clauses is **not** wrapped, so it must not be indented either.
     */
    const expression =
      block.visibility && !mode.ignoreVisibility
        ? compileCondition(block.visibility)
        : null;

    const markup = emitBlock(
      block,
      registry,
      warnings,
      expression === null ? depth : depth + 1,
      mode,
    );
    if (!markup) continue;

    if (block.visibility && expression === null && !mode.ignoreVisibility) {
      // A condition with no usable clauses would compile to `{% if %}`, a Twig
      // syntax error. Emitting the block unconditionally keeps the content
      // visible; the warning surfaces the misconfiguration.
      warnings.push({
        code: COMPILE_WARNING.EMPTY_CONDITION,
        blockId: block.id,
        detail: 'Visibility condition has no clauses; block always shown',
      });
    }

    if (expression === null) {
      parts.push(markup);
      continue;
    }

    /**
     * A condition over the provider's own variables has to survive our render
     * as text, or it is evaluated here — against a payload with no recipient in
     * it — and takes the block with it. See sendTime.ts.
     */
    const scope = conditionScope(block.visibility!, mode.sendTimeRoots);

    if (scope === 'mixed') {
      // One `{% if %}` runs in one pass or the other, so a condition naming both
      // cannot be honoured. Keeping it at push time risks a wrongly hidden
      // block; deferring it would put literal Twig — and our own
      // `{{ theme.… }}` with it — in front of the recipient.
      warnings.push({
        code: COMPILE_WARNING.MIXED_CONDITION_SCOPE,
        blockId: block.id,
        detail:
          'Condition mixes send-time and render-time variables; evaluated at render time',
      });
    }

    const open = `{% if ${expression} %}`;
    const [ifTag, endTag] =
      scope === 'send'
        ? [deferStatement(open), deferStatement('{% endif %}')]
        : [open, '{% endif %}'];

    parts.push(`${pad}${ifTag}\n${markup}\n${pad}${endTag}`);
  }

  return parts.join('\n');
}

export interface CompileContext {
  /** Block definitions derived from the fragment catalog; see registry.ts. */
  registry?: Registry;
  /**
   * Emit conditional blocks **unwrapped**, for the design canvas only.
   *
   * The canvas renders real Twig against the author's real sample data, so a block whose
   * condition is false emits nothing — leaving it selected, in the tree, and invisible.
   * The canvas used to work around that by feeding the *variables* invented values that
   * satisfied every clause, which meant adding a `customer.firstname` condition replaced
   * the name the block printed with a `1`: one variable cannot hold the value a condition
   * needs and the value the text should show at the same time. Dropping the `{% if %}`
   * instead keeps every block visible with nothing faked.
   *
   * Never for a body that is stored or sent — the conditions are the point there.
   */
  ignoreVisibility?: boolean;
  /** The `{{` picker's catalogue — the namespaces a condition can name. */
  variableGroups?: EmailVariableGroup[];
  /**
   * Which of those namespaces our own render resolves, as `/email-variables`
   * declares them. Without it every condition is treated as ours and evaluated
   * at push time — the behaviour before send-time conditionals existed, and the
   * safe direction to fail in. See sendTime.ts.
   */
  renderTimeNamespaces?: string[];
}

/**
 * Top-level blocks grouped by the layout region they fill, in first-seen order.
 *
 * A slot-based layout (`layout/xxxl/base` declares nine regions) is filled by a real
 * template in several places at once, so one `{% block %}` per used region is what
 * the output has to be. Order is first-seen because a Twig block *override* has no
 * positional meaning — the parent decides where each renders — so there is nothing
 * to gain by pretending the child controls placement, and first-seen keeps the
 * output deterministic.
 */
function groupByLayoutSlot(
  blocks: Block[],
  fallback: string,
): { slot: string; blocks: Block[] }[] {
  const groups: { slot: string; blocks: Block[] }[] = [];
  const bySlot = new Map<string, Block[]>();

  for (const block of blocks) {
    const slot = block.layoutSlot || fallback;
    let bucket = bySlot.get(slot);
    if (!bucket) {
      bucket = [];
      bySlot.set(slot, bucket);
      groups.push({ slot, blocks: bucket });
    }
    bucket.push(block);
  }

  return groups;
}

/** Every layout region a document's top-level blocks fill. */
export function layoutSlotsUsed(document: EmailDocument): string[] {
  if (!document.layoutKey || document.rawPassthrough) return [];
  const fallback = document.layoutSlot || DEFAULT_LAYOUT_SLOT;
  return groupByLayoutSlot(document.blocks, fallback).map(
    (group) => group.slot,
  );
}

export function compile(
  document: EmailDocument,
  context: CompileContext = {},
): CompileResult {
  const registry = context.registry ?? EMPTY_REGISTRY;
  const warnings: CompileWarning[] = [];
  // Layouted content sits inside a `{% block %}`, so it starts one level in.
  const layouted = Boolean(document.layoutKey) && !document.rawPassthrough;

  const roots = sendTimeRoots(
    context.variableGroups,
    context.renderTimeNamespaces,
  );

  const mode: EmitMode = {
    // **A contained body takes no markers.** `rawPassthrough` means "this body is
    // whatever it already is" — bracketing it would rewrite every legacy template the
    // first time it compiled, against the guarantee that opening one mutates nothing.
    // Such a document is one opaque block anyway, and structurally locked. (A layouted
    // document is never raw-passthrough, so the layouted branch always gets markers.)
    markers: !document.rawPassthrough,
    ignoreVisibility: context.ignoreVisibility === true,
    sendTimeRoots: roots,
    sendTimeKey: [...roots].sort().join(','),
  };

  // Only the block overrides — the layout itself travels as `layout_key` and is
  // prepended by the renderer (server and preview alike). A raw-passthrough
  // document is emitted verbatim: its body already carries whatever block
  // overrides its layout expects, and wrapping those in another block would
  // make Twig discard the entire email.
  const body = layouted
    ? groupByLayoutSlot(
        document.blocks,
        document.layoutSlot || DEFAULT_LAYOUT_SLOT,
      )
        .map((group) =>
          [
            `{% block ${group.slot} %}`,
            emitBlocks(group.blocks, registry, warnings, 1, mode),
            '{% endblock %}',
          ].join('\n'),
        )
        .join('\n')
    : emitBlocks(document.blocks, registry, warnings, 0, mode);

  // No envelope: the document is stored beside the body (design_document),
  // not inside it. Bodies saved before that column carry one; loadDocument
  // still reads it, and the backend strips it on their next save.
  return {
    body: `${body}\n`,
    warnings,
  };
}
